import React from "react";
import {useLocation} from "react-router-dom";
import {BaseType, HazardousState, HazardType, Material} from "../types/Enums";
import {translate, translateOther} from "../types/EnumTranslations";
import {
    BaseComponent, Building, BuildingSummary,
    HazardousModel, ValueStatus, ComponentTypes, SurfaceType, WindowType, DoorType
} from "../types/protocols";
import { BaseComponentImpl } from "../types/implementations";
import {WarningData} from "../Components/WarningViewComponent";
import { RELEVANT_BASE_TYPES } from "./baseTypeUtils";

/**
 * Get display name for a custom type
 * Matches iOS implementation: label first, then build from material/color, then fallback
 */
export function getCustomTypeDisplayName(
    customType: SurfaceType | WindowType | DoorType | null | undefined
): string {
    if (!customType) return '';
    
    // First check for label
    if (customType.label && customType.label.length > 0) {
        return customType.label;
    }
    
    // Build name from properties (material, color, etc.)
    const parts: string[] = [];
    
    // Add material if available
    if (customType.material?.value && Array.isArray(customType.material.value)) {
        const materialNames = customType.material.value
            .map((m: Material) => translate(m))
            .join(', ');
        if (materialNames) {
            parts.push(materialNames);
        }
    }
    
    // Add color if available
    if (customType.color?.value?.colorEnum) {
        parts.push(translate(customType.color.value.colorEnum));
    }
    
    // For WindowType, add pane type if available
    if ('paneType' in customType) {
        const windowType = customType as WindowType;
        if (windowType.paneType?.value) {
            parts.push(translate(windowType.paneType.value));
        }
    }
    
    // Return combined name or fallback
    if (parts.length > 0) {
        return parts.join(', ');
    }
    
    return 'Odefinierad typ';
}

/**
 * Get all components that belong to a specific room
 */
function getAllRoomComponents(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent } } } },
    roomKey: string
): BaseComponent[] {
    if (!building.model.structure?.components) return [];
    
    const components = Object.values(building.model.structure.components);
    
    return components.filter(component => {
        // Direct room membership
        if (component.roomKey === roomKey) {
            return true;
        }
        
        // Check if component belongs to room via parentSurface relationships
        if (component.parentSurface || component.parentSurface1 || component.parentSurface2) {
            // Find the parent surface component
            const parentSurfaceKey = component.parentSurface || component.parentSurface1 || component.parentSurface2;
            if (parentSurfaceKey?.value) {
                const parentSurfaceComponent = building.model.structure?.components?.[parentSurfaceKey.value];
                if (parentSurfaceComponent && parentSurfaceComponent.roomKey === roomKey) {
                    return true;
                }
            }
        }
        
        return false;
    });
}

/**
 * Extracts the building key from the URL.
 * Assumes the URL structure is /building/buildingKey/...
 * @returns {string | null} The building key, or null if not found.
 */
export const useBuildingKey = (): string | null => {
    const location = useLocation();
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const buildingKey = pathSegments.length >= 2 ? pathSegments[1] : null;
    return buildingKey;
};

const COLORS = {
    primary: '#3cb6df',
    primaryDark: '#369ec3',
    deepGreen: '#36c356',
    deepRed: '#c33636',
    primaryDarkHover: '#ffffff',
    purple: '#9636c3',
    secondary: '#555',
    background: '#f4f4f4',
    redOrange: '#fd795f',
    gold: '#c39836',
    yellow: '#f5c45f',
    text: '#000',
    highlight: '#e0f7fa',
    lightLightGrey: '#f7f6f7',
    lightGrey: '#EFEFEF',
    middleGrey: '#b3b3b3',
    breadcrumbBackground: '#001D60',
};

export const displayHazards = (hazardous?: HazardousModel): JSX.Element | null => {
    if (!hazardous) return null;

    const hazardEntries = Object.entries(hazardous).filter(
        ([_, state]) =>
            state === HazardousState.Suspected || state === HazardousState.Confirmed
    );

    if (hazardEntries.length === 0) return null;

    return (
        <>
            {hazardEntries.map(([hazard, state]) => (
                <>
                    {`${translateOther(hazard)} ${translateOther(state).toLowerCase()}`}<br/>
                </>
            ))}
        </>
    );
};

/**
 * Count windows that have a specific component as their parent
 */
export function countWindowsForComponent(componentKey: string, building: Building): number {
    let windowCount = 0;

    // Check all components for windows
    if (building.model?.structure?.components) {
        Object.values(building.model.structure.components).forEach(component => {
            if (component.type === BaseType.Window) {
                // Windows can have parentSurface, parentSurface1, or parentSurface2
                const parentSurfaces = [
                    (component as any).parentSurface?.value,
                    (component as any).parentSurface1?.value,
                    (component as any).parentSurface2?.value
                ].filter(Boolean);

                if (parentSurfaces.includes(componentKey)) {
                    windowCount++;
                }
            }
        });
    }

    return windowCount;
}

/**
 * Count doors that have a specific component as their parent
 */
export function countDoorsForComponent(componentKey: string, building: Building): number {
    let doorCount = 0;

    // Check all components for doors
    if (building.model?.structure?.components) {
        Object.values(building.model.structure.components).forEach(component => {
            if (component.type === BaseType.Door) {
                // Doors can have parentSurface, parentSurface1, or parentSurface2
                const parentSurfaces = [
                    (component as any).parentSurface?.value,
                    (component as any).parentSurface1?.value,
                    (component as any).parentSurface2?.value
                ].filter(Boolean);

                if (parentSurfaces.includes(componentKey)) {
                    doorCount++;
                }
            }
        });
    }

    return doorCount;
}

/**
 * Count sheet metal components that have a specific component as their parent
 */
export function countSheetMetalForComponent(componentKey: string, building: Building): number {
    let sheetMetalCount = 0;

    // Check all components for sheet metal
    if (building.model?.structure?.components) {
        Object.values(building.model.structure.components).forEach(component => {
            if (component.type === BaseType.SheetMetal) {
                // Sheet metal has a single parentSurface
                const parentSurface = (component as any).parentSurface?.value;

                if (parentSurface === componentKey) {
                    sheetMetalCount++;
                }
            }
        });
    }

    return sheetMetalCount;
}

/**
 * Count chimney components that have a specific component as their parent
 */
export function countChimneysForComponent(componentKey: string, building: Building): number {
    let chimneyCount = 0;

    // Check all components for chimneys
    if (building.model?.structure?.components) {
        Object.values(building.model.structure.components).forEach(component => {
            if (component.type === BaseType.Chimney) {
                // Chimney has a single parentSurface
                const parentSurface = (component as any).parentSurface?.value;

                if (parentSurface === componentKey) {
                    chimneyCount++;
                }
            }
        });
    }

    return chimneyCount;
}

/**
 * Count drainage components that have a specific component as their parent
 */
export function countDrainageForComponent(componentKey: string, building: Building): number {
    let drainageCount = 0;

    // Check all components for drainage
    if (building.model?.structure?.components) {
        Object.values(building.model.structure.components).forEach(component => {
            if (component.type === BaseType.RoofDrainage) {
                // Drainage has a single parentSurface
                const parentSurface = (component as any).parentSurface?.value;

                if (parentSurface === componentKey) {
                    drainageCount++;
                }
            }
        });
    }

    return drainageCount;
}

export function analyzeComponents(components: BaseComponent[], building?: Building): string[] {
    const analysis: string[] = [];

    analysis.push(components.length === 1 ? `${components.length} del` : `${components.length} delar`);

    let totalArea = components.reduce((sum, comp) => {
        if ('width' in comp && 'height' in comp &&
            typeof comp.width === 'number' &&
            typeof comp.height === 'number') {
            return sum + (comp.width * comp.height);
        }
        return sum;
    }, 0);

    totalArea = totalArea / 1000000;
    if (totalArea > 0) {
        analysis.push(`${totalArea.toFixed(2)} kvm`);
    }

    const allMaterials = new Set<Material>();
    components.forEach(comp => {
        if (comp.material && Array.isArray(comp.material)) {
            comp.material.forEach((mat: Material) => allMaterials.add(mat));
        }
    });

    if (allMaterials.size > 0) {
        const translatedMaterials = Array.from(allMaterials)
            .map(material => translate(material).toLowerCase())
            .join(', ');
        analysis.push(`Material: ${translatedMaterials}`);
    }

    // Count windows that have these components as their parent
    if (building) {
        const componentKeys = new Set(components.map(comp => comp.key));
        let windowCount = 0;
        let doorCount = 0;

        // Check all components for windows and doors
        if (building.model?.structure?.components) {
            Object.values(building.model.structure.components).forEach(component => {
                if (component.type === BaseType.Window) {
                    // Windows can have parentSurface, parentSurface1, or parentSurface2
                    const parentSurfaces = [
                        (component as any).parentSurface?.value,
                        (component as any).parentSurface1?.value,
                        (component as any).parentSurface2?.value
                    ].filter(Boolean);

                    if (parentSurfaces.some(surfaceKey => componentKeys.has(surfaceKey))) {
                        windowCount++;
                    }
                }
                
                if (component.type === BaseType.Door) {
                    // Doors can have parentSurface, parentSurface1, or parentSurface2
                    const parentSurfaces = [
                        (component as any).parentSurface?.value,
                        (component as any).parentSurface1?.value,
                        (component as any).parentSurface2?.value
                    ].filter(Boolean);

                    if (parentSurfaces.some(surfaceKey => componentKeys.has(surfaceKey))) {
                        doorCount++;
                    }
                }
            });
        }

        if (windowCount > 0) {
            analysis.push(windowCount === 1 ? `${windowCount} fönster` : `${windowCount} fönster`);
        }
        
        if (doorCount > 0) {
            analysis.push(doorCount === 1 ? `${doorCount} dörr` : `${doorCount} dörrar`);
        }
    }

    const uniqueRisks = new Set<string>();
    components.forEach(comp => {
        if (comp.buildingRisks?.value?.risks) {
            comp.buildingRisks.value?.risks.forEach(risk => {
                uniqueRisks.add(translate(risk));
            });
        }
        if (comp.buildingRisks?.value?.customRisk) {
            uniqueRisks.add(comp.buildingRisks.value?.customRisk);
        }
    });

    if (uniqueRisks.size > 0) {
        analysis.push('Riskkonstruktion:');
        Array.from(uniqueRisks).forEach(risk => {
            analysis.push(`- ${risk}`);
        });
    }

    const uniqueHazards = new Set<string>();
    components.forEach(comp => {
        if (comp.hazardous?.value) {
            const hazardModel = comp.hazardous.value;
            if (hazardModel.led) {
                uniqueHazards.add(`${translate(HazardType.HazardLed)} (${translate(hazardModel.led)})`);
            }
            if (hazardModel.asbestos) {
                uniqueHazards.add(`${translate(HazardType.HazardAsbestos)} (${translate(hazardModel.asbestos)})`);
            }
            if (hazardModel.arsenic) {
                uniqueHazards.add(`${translate(HazardType.HazardArsenic)} (${translate(hazardModel.arsenic)})`);
            }
            if (hazardModel.blueConcrete) {
                uniqueHazards.add(`${translate(HazardType.HazardBlueConcrete)} (${translate(hazardModel.blueConcrete)})`);
            }
            if (hazardModel.radon) {
                uniqueHazards.add(`${translate(HazardType.HazardRadon)} (${translate(hazardModel.radon)})`);
            }
        }
    });

    if (uniqueHazards.size > 0) {
        analysis.push('Hälso/miljöfara: ' + Array.from(uniqueHazards).join(', '));
    }

    return analysis;
}

/**
 * Function to get all unique risk types from a list of components, with translations
 * @param components Array or object of BaseComponent objects with risk information
 * @param translations Optional mapping of risk type to translated string
 * @returns Array of strings containing translated risk types and custom risks
 */
export function getAllUniqueRiskTypes(components: BaseComponent[] | { [key: string]: BaseComponent }): string[] {
    const uniqueRisks = new Set<string>();

    const componentArray = Array.isArray(components)
        ? components
        : Object.values(components);

    // Iterate through all components
    componentArray.forEach(component => {
        // Check if component has buildingRisks and its value is not null
        if (component.buildingRisks?.status === ValueStatus.set && component.buildingRisks.value) {
            const riskConstruction = component.buildingRisks.value;

            // Add standard risk types if present
            if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                riskConstruction.risks.forEach(risk => {
                    uniqueRisks.add(translate(risk));
                });
            }

            // Add custom risk if present
            if (riskConstruction.customRisk) {
                uniqueRisks.add(riskConstruction.customRisk);
            }
        }
    });

    // Convert Set to Array and return
    return Array.from(uniqueRisks);
}

export function getRiskWarnings(
    components: BaseComponent[] | { [key: string]: BaseComponent }
): WarningData[] {
    // Get all unique risk types
    const riskTypes = getAllUniqueRiskTypes(components);

    // Convert each risk type to a WarningData object
    return riskTypes.map(riskType => ({
        title: riskType,
        items: []
    }));
}

export interface RiskComponentInfo {
    key: string;
    type: string;
    label?: string;
    specificType?: string;
}

export interface RiskConstructionData {
    riskType: string;
    componentCount: number;
    components: RiskComponentInfo[];
}

export function getAllUniqueRiskTypesFromLevel(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any }, types?: ComponentTypes } } },
    levelKey: string
): RiskConstructionData[] {
    const riskMap = new Map<string, RiskComponentInfo[]>();
    
    // Get all rooms on this level
    const levelRooms = building.model.structure?.rooms 
        ? Object.values(building.model.structure.rooms).filter(room => room.levelKey === levelKey)
        : [];
    
    // Get all components that belong to any room on this level
    const levelComponents = levelRooms.flatMap(room => getAllRoomComponents(building, room.key));

    // Check components for risks
    levelComponents.forEach(component => {
        if (component.buildingRisks?.status === 'set' && component.buildingRisks.value) {
            const riskConstruction = component.buildingRisks.value;
            
            // Add standard risk types if present
            if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                riskConstruction.risks.forEach((risk: any) => {
                    const riskType = translate(risk);
                    if (!riskMap.has(riskType)) {
                        riskMap.set(riskType, []);
                    }
                    const componentImpl = new BaseComponentImpl(component);
                    riskMap.get(riskType)!.push({
                        key: component.key,
                        type: component.type,
                        label: componentImpl.displayName(building as Building),
                        specificType: component.specificType
                    });
                });
            }
            
            // Add custom risk if present
            if (riskConstruction.customRisk) {
                const riskType = riskConstruction.customRisk;
                if (!riskMap.has(riskType)) {
                    riskMap.set(riskType, []);
                }
                const componentImpl = new BaseComponentImpl(component);
                riskMap.get(riskType)!.push({
                    key: component.key,
                    type: component.type,
                    label: componentImpl.displayName(building as Building),
                    specificType: component.specificType
                });
            }
        }
    });

    // Check custom types for risks
    if (building.model.structure?.types) {
        const types = building.model.structure.types;
        
        levelComponents.forEach(component => {
            if (component.typeKey?.value) {
                let customType: SurfaceType | WindowType | DoorType | null = null;
                
                if (component.type === BaseType.Wall || component.type === BaseType.Ceiling || component.type === BaseType.Floor) {
                    customType = types.surfaceTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Window) {
                    customType = types.windowTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Door) {
                    customType = types.doorTypes?.[component.typeKey.value];
                }
                
                if (customType?.buildingRisks?.value) {
                    const riskConstruction = customType.buildingRisks.value;
                    
                    // Add standard risk types from custom type
                    if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                        riskConstruction.risks.forEach((risk: any) => {
                            const riskType = translate(risk);
                            if (!riskMap.has(riskType)) {
                                riskMap.set(riskType, []);
                            }
                            const componentImpl = new BaseComponentImpl(component);
                            riskMap.get(riskType)!.push({
                                key: component.key,
                                type: component.type,
                                label: componentImpl.displayName(building as Building),
                                specificType: component.specificType
                            });
                        });
                    }
                    
                    // Add custom risk from custom type
                    if (riskConstruction.customRisk) {
                        const riskType = riskConstruction.customRisk;
                        if (!riskMap.has(riskType)) {
                            riskMap.set(riskType, []);
                        }
                        const componentImpl = new BaseComponentImpl(component);
                        riskMap.get(riskType)!.push({
                            key: component.key,
                            type: component.type,
                            label: componentImpl.displayName(building as Building),
                            specificType: component.specificType
                        });
                    }
                }
            }
        });
    }

    return Array.from(riskMap.entries()).map(([riskType, components]) => ({
        riskType,
        componentCount: components.length,
        components
    }));
}

export function getAllUniqueRiskTypesFromRoom(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any }, types?: ComponentTypes } } },
    roomKey: string
): RiskConstructionData[] {
    const riskMap = new Map<string, RiskComponentInfo[]>();
    
    // Get all components that belong to this room
    const roomComponents = getAllRoomComponents(building, roomKey);

    // Check components for risks
    roomComponents.forEach(component => {
        if (component.buildingRisks?.status === 'set' && component.buildingRisks.value) {
            const riskConstruction = component.buildingRisks.value;
            
            // Add standard risk types if present
            if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                riskConstruction.risks.forEach((risk: any) => {
                    const riskType = translate(risk);
                    if (!riskMap.has(riskType)) {
                        riskMap.set(riskType, []);
                    }
                    const componentImpl = new BaseComponentImpl(component);
                    riskMap.get(riskType)!.push({
                        key: component.key,
                        type: component.type,
                        label: componentImpl.displayName(building as Building),
                        specificType: component.specificType
                    });
                });
            }
            
            // Add custom risk if present
            if (riskConstruction.customRisk) {
                const riskType = riskConstruction.customRisk;
                if (!riskMap.has(riskType)) {
                    riskMap.set(riskType, []);
                }
                const componentImpl = new BaseComponentImpl(component);
                riskMap.get(riskType)!.push({
                    key: component.key,
                    type: component.type,
                    label: componentImpl.displayName(building as Building),
                    specificType: component.specificType
                });
            }
        }
    });

    // Check custom types for risks
    if (building.model.structure?.types) {
        const types = building.model.structure.types;
        
        roomComponents.forEach(component => {
            if (component.typeKey?.value) {
                let customType: SurfaceType | WindowType | DoorType | null = null;
                
                if (component.type === BaseType.Wall || component.type === BaseType.Ceiling || component.type === BaseType.Floor) {
                    customType = types.surfaceTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Window) {
                    customType = types.windowTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Door) {
                    customType = types.doorTypes?.[component.typeKey.value];
                }
                
                if (customType?.buildingRisks?.value) {
                    const riskConstruction = customType.buildingRisks.value;
                    
                    // Add standard risk types from custom type
                    if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                        riskConstruction.risks.forEach((risk: any) => {
                            const riskType = translate(risk);
                            if (!riskMap.has(riskType)) {
                                riskMap.set(riskType, []);
                            }
                            const componentImpl = new BaseComponentImpl(component);
                            riskMap.get(riskType)!.push({
                                key: component.key,
                                type: component.type,
                                label: componentImpl.displayName(building as Building),
                                specificType: component.specificType
                            });
                        });
                    }
                    
                    // Add custom risk from custom type
                    if (riskConstruction.customRisk) {
                        const riskType = riskConstruction.customRisk;
                        if (!riskMap.has(riskType)) {
                            riskMap.set(riskType, []);
                        }
                        const componentImpl = new BaseComponentImpl(component);
                        riskMap.get(riskType)!.push({
                            key: component.key,
                            type: component.type,
                            label: componentImpl.displayName(building as Building),
                            specificType: component.specificType
                        });
                    }
                }
            }
        });
    }

    return Array.from(riskMap.entries()).map(([riskType, components]) => ({
        riskType,
        componentCount: components.length,
        components
    }));
}

export function getAllUniqueRiskTypesFromBuilding(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any }, types?: ComponentTypes } } }
): RiskConstructionData[] {
    const riskMap = new Map<string, RiskComponentInfo[]>();

    // Check components
    if (building.model.structure?.components) {
        const componentArray = Object.values(building.model.structure.components);
        componentArray.forEach(component => {
            if (component.buildingRisks?.status === 'set' && component.buildingRisks.value) {
                const riskConstruction = component.buildingRisks.value;
                
                // Add standard risk types if present
                if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                    riskConstruction.risks.forEach((risk: any) => {
                        const riskType = translate(risk);
                        if (!riskMap.has(riskType)) {
                            riskMap.set(riskType, []);
                        }
                        const componentImpl = new BaseComponentImpl(component);
                        riskMap.get(riskType)!.push({
                            key: component.key,
                            type: component.type,
                            label: componentImpl.displayName(building as Building),
                            specificType: component.specificType
                        });
                    });
                }
                
                // Add custom risk if present
                if (riskConstruction.customRisk) {
                    const riskType = riskConstruction.customRisk;
                    if (!riskMap.has(riskType)) {
                        riskMap.set(riskType, []);
                    }
                    riskMap.get(riskType)!.push({
                        key: component.key,
                        type: component.type,
                        label: component.label,
                        specificType: component.specificType
                    });
                }
            }
        });

        // Check custom types for risks
        if (building.model.structure?.types) {
            const types = building.model.structure.types;
            
            // Check each component for typeKey and look up custom type risks
            componentArray.forEach(component => {
                if (component.typeKey?.value) {
                    const typeKey = component.typeKey.value;
                    let customType: any = null;
                    
                    // Find the custom type based on component type
                    if (component.type === 'wall' || component.type === 'floor' || component.type === 'ceiling') {
                        customType = types.surfaceTypes?.[typeKey];
                    } else if (component.type === 'window') {
                        customType = types.windowTypes?.[typeKey];
                    } else if (component.type === 'door') {
                        customType = types.doorTypes?.[typeKey];
                    }
                    
                    // Add risks from custom type if present
                    if (customType?.buildingRisks?.value) {
                        const riskConstruction = customType.buildingRisks.value;
                        
                        // Add standard risk types if present
                        if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                            riskConstruction.risks.forEach((risk: any) => {
                                const riskType = translate(risk);
                                if (!riskMap.has(riskType)) {
                                    riskMap.set(riskType, []);
                                }
                                const componentImpl = new BaseComponentImpl(component);
                                riskMap.get(riskType)!.push({
                                    key: component.key,
                                    type: component.type,
                                    label: componentImpl.displayName(building as Building),
                                    specificType: component.specificType
                                });
                            });
                        }
                        
                        // Add custom risk if present
                        if (riskConstruction.customRisk) {
                            const riskType = riskConstruction.customRisk;
                            if (!riskMap.has(riskType)) {
                                riskMap.set(riskType, []);
                            }
                            riskMap.get(riskType)!.push({
                                key: component.key,
                                type: component.type,
                                label: component.label || getCustomTypeDisplayName(customType),
                                specificType: component.specificType
                            });
                        }
                    }
                }
            });
        }
    }

    // Check rooms (if they have risk construction fields in the future)
    if (building.model.structure?.rooms) {
        const roomArray = Object.values(building.model.structure.rooms);
        roomArray.forEach(room => {
            // Currently rooms don't have risk construction fields, but this is prepared for future expansion
            if (room.buildingRisks?.status === 'set' && room.buildingRisks.value) {
                const riskConstruction = room.buildingRisks.value;
                if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                    riskConstruction.risks.forEach((risk: any) => {
                        const riskType = translate(risk);
                        if (!riskMap.has(riskType)) {
                            riskMap.set(riskType, []);
                        }
                        riskMap.get(riskType)!.push({
                            key: room.key,
                            type: 'room',
                            label: room.label?.value,
                            specificType: room.type
                        });
                    });
                }
                if (riskConstruction.customRisk) {
                    const riskType = riskConstruction.customRisk;
                    if (!riskMap.has(riskType)) {
                        riskMap.set(riskType, []);
                    }
                    riskMap.get(riskType)!.push({
                        key: room.key,
                        type: 'room',
                        label: room.label?.value,
                        specificType: room.type
                    });
                }
            }
        });
    }

    // Check levels (if they have risk construction fields in the future)
    if (building.model.structure?.levels) {
        const levelArray = Object.values(building.model.structure.levels);
        levelArray.forEach(level => {
            // Currently levels don't have risk construction fields, but this is prepared for future expansion
            if (level.buildingRisks?.status === 'set' && level.buildingRisks.value) {
                const riskConstruction = level.buildingRisks.value;
                if (riskConstruction.risks && riskConstruction.risks.length > 0) {
                    riskConstruction.risks.forEach((risk: any) => {
                        const riskType = translate(risk);
                        if (!riskMap.has(riskType)) {
                            riskMap.set(riskType, []);
                        }
                        riskMap.get(riskType)!.push({
                            key: level.key,
                            type: 'level',
                            label: level.name,
                            specificType: level.type
                        });
                    });
                }
                if (riskConstruction.customRisk) {
                    const riskType = riskConstruction.customRisk;
                    if (!riskMap.has(riskType)) {
                        riskMap.set(riskType, []);
                    }
                    riskMap.get(riskType)!.push({
                        key: level.key,
                        type: 'level',
                        label: level.name,
                        specificType: level.type
                    });
                }
            }
        });
    }

    // Convert map to array of RiskConstructionData
    return Array.from(riskMap.entries()).map(([riskType, components]) => ({
        riskType,
        componentCount: components.length,
        components
    }));
}

export function getRiskWarningsFromBuilding(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any } } } }
): RiskConstructionData[] {
    // Get all unique risk types with component details from the entire building
    return getAllUniqueRiskTypesFromBuilding(building);
}

export function getAllUniqueHazards(
    components: BaseComponent[] | { [key: string]: BaseComponent }
): Map<string, HazardousState> {
    const hazardMap = new Map<string, HazardousState>();

    // Convert to array if object is provided
    const componentArray = Array.isArray(components)
        ? components
        : Object.values(components);

    // Iterate through all components
    componentArray.forEach(component => {
        // Check if component has hazardous and its value is not null
        if (component.hazardous?.status === ValueStatus.set && component.hazardous.value) {
            const hazardModel = component.hazardous.value;

            // Process each hazard type
            if (hazardModel.asbestos) {
                updateHazardMap(hazardMap, HazardType.HazardAsbestos, hazardModel.asbestos);
            }
            if (hazardModel.pcb) {
                updateHazardMap(hazardMap, HazardType.HazardPcb, hazardModel.pcb);
            }
            if (hazardModel.led) {
                updateHazardMap(hazardMap, HazardType.HazardLed, hazardModel.led);
            }
            if (hazardModel.arsenic) {
                updateHazardMap(hazardMap, HazardType.HazardArsenic, hazardModel.arsenic);
            }
            if (hazardModel.blueConcrete) {
                updateHazardMap(hazardMap, HazardType.HazardBlueConcrete, hazardModel.blueConcrete);
            }
            if (hazardModel.radon) {
                updateHazardMap(hazardMap, HazardType.HazardRadon, hazardModel.radon);
            }
        }
    });

    return hazardMap;
}

/**
 * Helper function to update the hazard map
 * @param map The map to update
 * @param hazardKey The key for the hazard
 * @param status The new status to consider
 */
function updateHazardMap(
    map: Map<string, HazardousState>,
    hazardKey: string,
    status: HazardousState
): void {
    // If not in map yet, add it
    if (!map.has(hazardKey)) {
        map.set(hazardKey, status);
        return;
    }

    // If already CONFIRMED, keep it
    if (map.get(hazardKey) === HazardousState.Confirmed) {
        return;
    }

    // If new status is CONFIRMED, update it (CONFIRMED takes precedence over SUSPECTED)
    if (status === HazardousState.Confirmed) {
        map.set(hazardKey, HazardousState.Confirmed);
    }
}

/**
 * Get all unique hazards from a list of components
 * @param components Array or object of BaseComponent objects with hazard information
 * @returns Array of WarningData objects for hazards
 */
export function getHazardWarnings(
    components: BaseComponent[] | { [key: string]: BaseComponent }
): WarningData[] {
    const hazardMap = getAllUniqueHazards(components);
    const warningData: WarningData[] = [];

    // Convert hazard map to warning data
    hazardMap.forEach((state, hazardKey) => {
        warningData.push({
            title: `${translate(hazardKey)} - ${translate(state)}`,
            items: []
        });
    });

    return warningData;
}

export interface HazardComponentInfo {
    key: string;
    type: string;
    label?: string;
    specificType?: string;
    hazardState: string;
}

export interface HazardData {
    hazardType: string;
    componentCount: number;
    components: HazardComponentInfo[];
}

/**
 * Get all unique hazards with component details from a building
 * @param building Building object with components and custom types
 * @returns Array of HazardData objects with component details
 */
export function getAllUniqueHazardsFromLevel(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any }, types?: ComponentTypes } } },
    levelKey: string
): HazardData[] {
    const hazardMap = new Map<string, HazardComponentInfo[]>();
    
    // Get all rooms on this level
    const levelRooms = building.model.structure?.rooms 
        ? Object.values(building.model.structure.rooms).filter(room => room.levelKey === levelKey)
        : [];
    
    // Get all components that belong to any room on this level
    const levelComponents = levelRooms.flatMap(room => getAllRoomComponents(building, room.key));

    // Check components for hazards
    levelComponents.forEach(component => {
        // Check if component has hazardous and its value is not null
        if (component.hazardous?.status === ValueStatus.set && component.hazardous.value) {
            const hazardModel = component.hazardous.value;

            // Process each hazard type
            if (hazardModel.asbestos) {
                const hazardKey = translate(HazardType.HazardAsbestos);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.asbestos);
            }
            if (hazardModel.pcb) {
                const hazardKey = translate(HazardType.HazardPcb);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.pcb);
            }
            if (hazardModel.led) {
                const hazardKey = translate(HazardType.HazardLed);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.led);
            }
            if (hazardModel.arsenic) {
                const hazardKey = translate(HazardType.HazardArsenic);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.arsenic);
            }
            if (hazardModel.blueConcrete) {
                const hazardKey = translate(HazardType.HazardBlueConcrete);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.blueConcrete);
            }
            if (hazardModel.radon) {
                const hazardKey = translate(HazardType.HazardRadon);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.radon);
            }
        }
    });

    // Check custom types for hazards
    if (building.model.structure?.types) {
        const types = building.model.structure.types;
        
        levelComponents.forEach(component => {
            if (component.typeKey?.value) {
                let customType: SurfaceType | WindowType | DoorType | null = null;
                
                if (component.type === BaseType.Wall || component.type === BaseType.Ceiling || component.type === BaseType.Floor) {
                    customType = types.surfaceTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Window) {
                    customType = types.windowTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Door) {
                    customType = types.doorTypes?.[component.typeKey.value];
                }
                
                // Add hazards from custom type if present
                if (customType?.hazardous?.value) {
                    const hazardModel = customType.hazardous.value;

                    // Process each hazard type from custom type
                    if (hazardModel.asbestos) {
                        const hazardKey = translate(HazardType.HazardAsbestos);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.asbestos);
                    }
                    if (hazardModel.pcb) {
                        const hazardKey = translate(HazardType.HazardPcb);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.pcb);
                    }
                    if (hazardModel.led) {
                        const hazardKey = translate(HazardType.HazardLed);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.led);
                    }
                    if (hazardModel.arsenic) {
                        const hazardKey = translate(HazardType.HazardArsenic);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.arsenic);
                    }
                    if (hazardModel.blueConcrete) {
                        const hazardKey = translate(HazardType.HazardBlueConcrete);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.blueConcrete);
                    }
                    if (hazardModel.radon) {
                        const hazardKey = translate(HazardType.HazardRadon);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.radon);
                    }
                }
            }
        });
    }

    return Array.from(hazardMap.entries()).map(([hazardType, components]) => ({
        hazardType,
        componentCount: components.length,
        components
    }));
}

export function getAllUniqueHazardsFromRoom(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any }, types?: ComponentTypes } } },
    roomKey: string
): HazardData[] {
    const hazardMap = new Map<string, HazardComponentInfo[]>();
    
    // Get all components that belong to this room
    const roomComponents = getAllRoomComponents(building, roomKey);

    // Check components for hazards
    roomComponents.forEach(component => {
        // Check if component has hazardous and its value is not null
        if (component.hazardous?.status === ValueStatus.set && component.hazardous.value) {
            const hazardModel = component.hazardous.value;

            // Process each hazard type
            if (hazardModel.asbestos) {
                const hazardKey = translate(HazardType.HazardAsbestos);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.asbestos);
            }
            if (hazardModel.pcb) {
                const hazardKey = translate(HazardType.HazardPcb);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.pcb);
            }
            if (hazardModel.led) {
                const hazardKey = translate(HazardType.HazardLed);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.led);
            }
            if (hazardModel.arsenic) {
                const hazardKey = translate(HazardType.HazardArsenic);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.arsenic);
            }
            if (hazardModel.blueConcrete) {
                const hazardKey = translate(HazardType.HazardBlueConcrete);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.blueConcrete);
            }
            if (hazardModel.radon) {
                const hazardKey = translate(HazardType.HazardRadon);
                addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.radon);
            }
        }
    });

    // Check custom types for hazards
    if (building.model.structure?.types) {
        const types = building.model.structure.types;
        
        roomComponents.forEach(component => {
            if (component.typeKey?.value) {
                let customType: SurfaceType | WindowType | DoorType | null = null;
                
                if (component.type === BaseType.Wall || component.type === BaseType.Ceiling || component.type === BaseType.Floor) {
                    customType = types.surfaceTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Window) {
                    customType = types.windowTypes?.[component.typeKey.value];
                } else if (component.type === BaseType.Door) {
                    customType = types.doorTypes?.[component.typeKey.value];
                }
                
                // Add hazards from custom type if present
                if (customType?.hazardous?.value) {
                    const hazardModel = customType.hazardous.value;

                    // Process each hazard type from custom type
                    if (hazardModel.asbestos) {
                        const hazardKey = translate(HazardType.HazardAsbestos);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.asbestos);
                    }
                    if (hazardModel.pcb) {
                        const hazardKey = translate(HazardType.HazardPcb);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.pcb);
                    }
                    if (hazardModel.led) {
                        const hazardKey = translate(HazardType.HazardLed);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.led);
                    }
                    if (hazardModel.arsenic) {
                        const hazardKey = translate(HazardType.HazardArsenic);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.arsenic);
                    }
                    if (hazardModel.blueConcrete) {
                        const hazardKey = translate(HazardType.HazardBlueConcrete);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.blueConcrete);
                    }
                    if (hazardModel.radon) {
                        const hazardKey = translate(HazardType.HazardRadon);
                        addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.radon);
                    }
                }
            }
        });
    }

    return Array.from(hazardMap.entries()).map(([hazardType, components]) => ({
        hazardType,
        componentCount: components.length,
        components
    }));
}

export function getAllUniqueHazardsFromBuilding(
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any }, types?: ComponentTypes } } }
): HazardData[] {
    const hazardMap = new Map<string, HazardComponentInfo[]>();

    // Check components
    if (building.model.structure?.components) {
        const componentArray = Object.values(building.model.structure.components);
        componentArray.forEach(component => {
            // Check if component has hazardous and its value is not null
            if (component.hazardous?.status === ValueStatus.set && component.hazardous.value) {
                const hazardModel = component.hazardous.value;

                // Process each hazard type
                if (hazardModel.asbestos) {
                    const hazardKey = translate(HazardType.HazardAsbestos);
                    addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.asbestos);
                }
                if (hazardModel.pcb) {
                    const hazardKey = translate(HazardType.HazardPcb);
                    addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.pcb);
                }
                if (hazardModel.led) {
                    const hazardKey = translate(HazardType.HazardLed);
                    addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.led);
                }
                if (hazardModel.arsenic) {
                    const hazardKey = translate(HazardType.HazardArsenic);
                    addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.arsenic);
                }
                if (hazardModel.blueConcrete) {
                    const hazardKey = translate(HazardType.HazardBlueConcrete);
                    addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.blueConcrete);
                }
                if (hazardModel.radon) {
                    const hazardKey = translate(HazardType.HazardRadon);
                    addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.radon);
                }
            }
        });

        // Check custom types for hazards
        if (building.model.structure?.types) {
            const types = building.model.structure.types;
            
            // Check each component for typeKey and look up custom type hazards
            componentArray.forEach(component => {
                if (component.typeKey?.value) {
                    const typeKey = component.typeKey.value;
                    let customType: SurfaceType | WindowType | DoorType | null = null;
                    
                    // Find the custom type based on component type
                    if (component.type === BaseType.Wall || component.type === BaseType.Floor || component.type === BaseType.Ceiling) {
                        customType = types.surfaceTypes?.[typeKey];
                    } else if (component.type === BaseType.Window) {
                        customType = types.windowTypes?.[typeKey];
                    } else if (component.type === BaseType.Door) {
                        customType = types.doorTypes?.[typeKey];
                    }
                    
                    // Add hazards from custom type if present
                    if (customType?.hazardous?.value) {
                        const hazardModel = customType.hazardous.value;

                        // Process each hazard type from custom type
                        if (hazardModel.asbestos) {
                            const hazardKey = translate(HazardType.HazardAsbestos);
                            addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.asbestos);
                        }
                        if (hazardModel.pcb) {
                            const hazardKey = translate(HazardType.HazardPcb);
                            addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.pcb);
                        }
                        if (hazardModel.led) {
                            const hazardKey = translate(HazardType.HazardLed);
                            addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.led);
                        }
                        if (hazardModel.arsenic) {
                            const hazardKey = translate(HazardType.HazardArsenic);
                            addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.arsenic);
                        }
                        if (hazardModel.blueConcrete) {
                            const hazardKey = translate(HazardType.HazardBlueConcrete);
                            addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.blueConcrete);
                        }
                        if (hazardModel.radon) {
                            const hazardKey = translate(HazardType.HazardRadon);
                            addHazardToMap(hazardMap, hazardKey, component, building, hazardModel.radon);
                        }
                    }
                }
            });
        }
    }

    // Convert map to array format
    const hazardData: HazardData[] = [];
    hazardMap.forEach((components, hazardKey) => {
        hazardData.push({
            hazardType: hazardKey,
            componentCount: components.length,
            components: components
        });
    });

    return hazardData.sort((a, b) => a.hazardType.localeCompare(b.hazardType));
}

/**
 * Helper function to add hazard to map
 */
function addHazardToMap(
    hazardMap: Map<string, HazardComponentInfo[]>, 
    hazardKey: string, 
    component: BaseComponent,
    building: { model: { structure?: { components?: { [key: string]: BaseComponent }, rooms?: { [key: string]: any }, levels?: { [key: string]: any }, types?: ComponentTypes } } },
    hazardState?: HazardousState
) {
    if (!hazardMap.has(hazardKey)) {
        hazardMap.set(hazardKey, []);
    }
    // Use the component's displayName method which handles custom types properly
    const componentImpl = new BaseComponentImpl(component);
    
    hazardMap.get(hazardKey)!.push({
        key: component.key,
        type: component.type,
        label: componentImpl.displayName(building as Building),
        specificType: component.specificType,
        hazardState: hazardState ? translate(hazardState) : ''
    });
}

// Helper function to filter components by type
const filterComponentsByType = (components: { [key: string]: BaseComponent } = {}, type: BaseType): BaseComponent[] =>
    Object.values(components).filter(component => component.type === type);

// Helper function to filter components by room and type
const filterComponentsByRoomAndType = (components: { [key: string]: BaseComponent } = {}, type: BaseType, roomKey: string): BaseComponent[] =>
    Object.values(components)
        .filter(component => component.type === type)
        .filter(component => component.parentRoomKeys?.value?.some(key => key === roomKey));

// Helper function to count photos in components
const countPhotosInComponents = (components: BaseComponent[]): number => {
    return components.reduce((total, component) => {
        const photoCount = component.photos ? Object.values(component.photos).length : 0;
        return total + photoCount;
    }, 0);
};

// Main helper function to count all images in a building
export const countBuildingImages = (building: Building): number => {
    const structure = building.model.structure;
    if (!structure) {
        return 0;
    }

    let totalImageCount = 0;

    // Count building component sections
    const componentTypes = RELEVANT_BASE_TYPES;

    for (const type of componentTypes) {
        const components = filterComponentsByType(structure.components, type);
        totalImageCount += countPhotosInComponents(components);
    }

    // Count room images
    if (structure.rooms) {
        Object.values(structure.rooms).forEach(room => {
            // Count room overview photos
            if (room.photos) {
                totalImageCount += Object.values(room.photos).length;
            }

            // Count room component photos (walls, ceiling, floor)
            const roomComponents = [
                ...filterComponentsByRoomAndType(structure.components, BaseType.Wall, room.key),
                ...filterComponentsByRoomAndType(structure.components, BaseType.Ceiling, room.key),
                ...filterComponentsByRoomAndType(structure.components, BaseType.Floor, room.key)
            ];

            totalImageCount += countPhotosInComponents(roomComponents);
        });
    }

    return totalImageCount;
};

export function getCoverPhotoStoragePath(building: BuildingSummary): string | undefined {
    if (building.currentCoverPhotoKey && building.coverPhotos) {
        const currentCoverPhoto = building.coverPhotos[building.currentCoverPhotoKey];
        if (currentCoverPhoto?.storagePath) {
            return currentCoverPhoto.storagePath;
        }
    }

    if (building.coverPhoto?.storagePath) {
        return building.coverPhoto.storagePath;
    }

    return undefined;
}

/**
 * Format level number with leading zero for single digits
 * Examples: 1 -> "01", 10 -> "10", -1 -> "-01", -10 -> "-10"
 */
export function formatLevelNumber(levelNumber: number | string): string {
    const num = typeof levelNumber === 'string' ? parseInt(levelNumber, 10) : levelNumber;
    
    if (isNaN(num)) {
        return String(levelNumber);
    }
    
    // Handle negative numbers
    if (num < 0) {
        const absNum = Math.abs(num);
        // Add leading zero for single digit negative numbers
        return absNum < 10 ? `-0${absNum}` : String(num);
    }
    
    // Add leading zero for single digit positive numbers
    return num < 10 ? `0${num}` : String(num);
}

export default COLORS;